import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Grid, List, Users, ChevronDown, X, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/Button';
import { SecondaryButton } from '../components/ui/SecondaryButton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Slider } from '../components/ui/slider';
import TalentListItem from '../components/TalentShowcase/TalentListItem';
import ApiBasedProfileCard from '../components/TalentProfile/ApiBasedProfileCard';
import { TalentCardData as ApiTalentCardData } from '../components/TalentProfile/ApiBasedProfileCard';
import AppLayout from '../components/Layout/AppLayout';
import UploadModal from '../components/UploadModal';
import { jobLevels, locations } from '../data/mockTalents';
import { Talent, TalentFilters } from '../types/talent';
import { TalentCardData } from '../types';
import { useTalentCardsList } from '../hooks/useTalentCardsList';
import { isAdminAtom } from '../store/auth';

// Transform TalentCardData to ApiTalentCardData interface
const transformTalentCardToApiFormat = (talentCard: TalentCardData): ApiTalentCardData => {
  const getAverageScore = (): number => {
    // First check if average_score is directly available in metrics
    if (talentCard.metrics?.average_score !== undefined && talentCard.metrics?.average_score !== null) {
      return talentCard.metrics.average_score;
    }

    // Otherwise calculate from scoringResults
    if (!talentCard.scoringResults) return 0;
    const dimensions = Object.values(talentCard.scoringResults) as any[];
    if (dimensions.length === 0) return 0;
    const avgScore = dimensions.reduce((sum, dim) => sum + (dim.score || 0), 0) / dimensions.length;
    return Math.round(avgScore * 10) / 10;
  };

  // Get TFCs directly from the API response
  const getTFCs = (): string | string[] => {
    // Check if TFCs are available in extractedDocumentData
    if (talentCard.extractedDocumentData?.employee_profile?.tfcs) {
      return talentCard.extractedDocumentData.employee_profile.tfcs;
    }
    return 'N/A';
  };

  return {
    id: talentCard.id,
    status: talentCard.status || 'completed',
    progress: 100,
    employee_name: talentCard.employeeProfile.name,
    current_stage: 'Completed',
    start_time: talentCard.uploadedAt.toISOString(),
    estimated_time_remaining: 0,
    error: null,
    processing_duration: talentCard.processingDuration || talentCard.processingMetadata?.total_processing_time || '2.3 min',
    file_name: talentCard.fileName,
    created_by: talentCard.metrics?.created_by || '',
    user_id: talentCard.metrics?.user_id || '',
    level: talentCard.employeeProfile.level,
    role: talentCard.employeeProfile.role,
    team: talentCard.employeeProfile.team,
    tenure: talentCard.employeeProfile.tenure,
    manager: talentCard.employeeProfile.manager,
    tfcs: getTFCs(),
    average_score: getAverageScore(),
    total_dimensions: talentCard.scoringResults ? Object.keys(talentCard.scoringResults).length : 6,
    key_strengths: talentCard.summary?.key_strengths || talentCard.calibrationInsights?.strengthAreas || [],
    development_areas: talentCard.summary?.development_areas || talentCard.calibrationInsights?.developmentAreas || [],
    confidence: talentCard.calibrationInsights?.confidence || 0.85,
    overall_assessment: talentCard.summary?.overall_assessment || talentCard.calibrationInsights?.keyInsights?.join('. ') || 'Assessment in progress',
    processed_date: talentCard.processedAt?.toISOString() || new Date().toISOString()
  };
};

// Transform TalentCardData to Talent interface
const transformTalentCardToTalent = (talentCard: TalentCardData): Talent => {
  const getStatusFromCalibration = (calibration?: string): Talent['status'] => {
    if (!calibration) return 'Performing at level';
    switch (calibration.toLowerCase()) {
      case 'above': return 'Above level';
      case 'below': return 'Below level';
      default: return 'Performing at level';
    }
  };

  const getSkillsFromAccomplishments = (accomplishments: string[]): string[] => {
    // Extract skills from accomplishments or use default skills
    const defaultSkills = ['Leadership', 'Communication', 'Problem Solving'];
    return accomplishments.length > 0 ? accomplishments.slice(0, 3) : defaultSkills;
  };

  const calculateRating = (): number => {
    // Use the same logic as getAverageScore to ensure consistency
    // First check if average_score is directly available in metrics
    if (talentCard.metrics?.average_score !== undefined && talentCard.metrics?.average_score !== null) {
      return talentCard.metrics.average_score;
    }

    // Otherwise calculate from scoringResults
    if (!talentCard.scoringResults) return 0;
    const dimensions = Object.values(talentCard.scoringResults) as any[];
    if (dimensions.length === 0) return 0;
    const avgScore = dimensions.reduce((sum, dim) => sum + (dim.score || 0), 0) / dimensions.length;
    return Math.round(avgScore * 10) / 10;
  };

  return {
    id: talentCard.id,
    name: talentCard.employeeProfile.name,
    jobTitle: talentCard.employeeProfile.role,
    jobLevel: talentCard.employeeProfile.level,
    department: talentCard.employeeProfile.team,
    location: 'Remote', // Default location as it's not in TalentCardData
    experience: talentCard.employeeProfile.tenure,
    skills: getSkillsFromAccomplishments(talentCard.keyAccomplishments),
    status: getStatusFromCalibration(talentCard.calibrationInsights?.overallCalibration),
    lastUpdated: talentCard.processedAt ? talentCard.processedAt.toISOString() : new Date().toISOString(),
    rating: calculateRating(),
    summary: talentCard.calibrationInsights?.keyInsights?.join('. ') || 'No summary available'
  };
};

const TalentShowcase: React.FC = () => {
  const navigate = useNavigate();
  const [isAdmin] = useAtom(isAdminAtom);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('all'); // For admin user filtering
  const [filters, setFilters] = useState<TalentFilters>({
    department: 'all',
    jobLevel: 'all',
    location: 'all',
    status: 'all',
    skills: [],
    scoreRange: [0, 5],
    dateRange: { from: undefined, to: undefined }
  });
  const { talentCards, loading, error, refresh } = useTalentCardsList();

  const handleDeleteTalent = async (talentId: string) => {
    // Refresh the talent list after deletion
    await refresh();
  };

  // Get unique users for filter dropdown (admin only)
  const uniqueUsers = useMemo(() => {
    if (!isAdmin) return [];

    const userMap = new Map<string, string>();
    talentCards.forEach(card => {
      // Only require user_id to be present
      if (card.metrics?.user_id) {
        // Use created_by if available, otherwise show "Unknown User" with user_id
        const displayName = card.metrics.created_by || `Unknown User (${card.metrics.user_id.substring(0, 8)})`;
        userMap.set(card.metrics.user_id, displayName);
      }
    });

    return Array.from(userMap.entries()).map(([userId, email]) => ({
      userId,
      email
    }));
  }, [talentCards, isAdmin]);

  // Transform and filter talents based on current filters
  const filteredTalents = useMemo(() => {
    let filteredCards = talentCards.filter(card => card.status === 'completed'); // Only show completed talent cards

    // Filter by selected user (admin only)
    if (isAdmin && selectedUser !== 'all') {
      filteredCards = filteredCards.filter(card => card.metrics?.user_id === selectedUser);
    }

    const talents = filteredCards.map(transformTalentCardToTalent);

    return talents.filter(talent => {
      const matchesSearch = talent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           talent.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           talent.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesDepartment = !filters.department || filters.department === 'all' || talent.department === filters.department;
      const matchesJobLevel = !filters.jobLevel || filters.jobLevel === 'all' || talent.jobLevel === filters.jobLevel;

      const matchesScoreRange = !filters.scoreRange ||
                                !talent.rating ||
                                (talent.rating >= filters.scoreRange[0] && talent.rating <= filters.scoreRange[1]);

      // Date range filtering based on lastUpdated
      const matchesDateRange = (() => {
        if (!filters.dateRange?.from && !filters.dateRange?.to) return true;

        const talentDate = new Date(talent.lastUpdated);
        const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
        const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;

        if (fromDate && toDate) {
          // Set time to start/end of day for accurate comparison
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(23, 59, 59, 999);
          return talentDate >= fromDate && talentDate <= toDate;
        } else if (fromDate) {
          fromDate.setHours(0, 0, 0, 0);
          return talentDate >= fromDate;
        }
        return true;
      })();

      return matchesSearch && matchesDepartment && matchesJobLevel && matchesScoreRange && matchesDateRange;
    });
  }, [talentCards, searchTerm, filters, isAdmin, selectedUser]);

  const handleTalentClick = (talent: Talent) => {
    navigate(`/talents/${talent.id}`);
  };

  const clearFilters = () => {
    setFilters({
      department: 'all',
      jobLevel: 'all',
      location: 'all',
      status: 'all',
      skills: [],
      scoreRange: [0, 5],
      dateRange: { from: undefined, to: undefined }
    });
    setSearchTerm('');
  };

  const activeFiltersCount = (() => {
    let count = 0;

    // Count non-default filters
    if (filters.department && filters.department !== 'all') count++;
    if (filters.jobLevel && filters.jobLevel !== 'all') count++;
    if (filters.location && filters.location !== 'all') count++;
    if (filters.status && filters.status !== 'all') count++;
    if (filters.skills && filters.skills.length > 0) count++;

    // Only count scoreRange if it's not the default [0, 5]
    if (filters.scoreRange && (filters.scoreRange[0] !== 0 || filters.scoreRange[1] !== 5)) count++;

    // Only count dateRange if from or to is set
    if (filters.dateRange && (filters.dateRange.from || filters.dateRange.to)) count++;

    // Count search term
    if (searchTerm) count++;

    return count;
  })();

  return (
    <AppLayout
      pageTitle="Talent Showcase"
      breadcrumb="Talent Showcase"
      onUploadClick={() => setShowUploadModal(true)}
    >
      <div className="flex flex-col h-full">
        {/* Minimal Filter Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex gap-2 items-center justify-between">
            {/* Filters - scrollable on mobile */}
            <div className="flex gap-2 items-center overflow-x-auto flex-1 min-w-0 scrollbar-hide">

            <Select value={filters.jobLevel} onValueChange={(value) => setFilters(prev => ({ ...prev, jobLevel: value }))}>
              <SelectTrigger className="w-20 sm:w-24 md:w-28 shrink-0">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Levels</SelectItem>
                {jobLevels.map(level => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* User Filter (Admin Only) */}
            {isAdmin && uniqueUsers.length > 0 && (
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-32 sm:w-40 md:w-48 shrink-0">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {uniqueUsers.map(user => (
                    <SelectItem key={user.userId} value={user.userId}>
                      {user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Score Range Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex h-10 w-20 sm:w-24 md:w-28 shrink-0 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-[var(--font-size-sm)] ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="line-clamp-1">
                    {filters.scoreRange?.[0] === 0 && filters.scoreRange?.[1] === 5
                      ? 'Score'
                      : `${filters.scoreRange?.[0].toFixed(1)} - ${filters.scoreRange?.[1].toFixed(1)}`}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-64" align="start">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Score Range</h4>
                    <div className="text-sm text-gray-600">
                      {filters.scoreRange?.[0].toFixed(1)} - {filters.scoreRange?.[1].toFixed(1)}
                    </div>
                  </div>
                  <Slider
                    value={filters.scoreRange || [0, 5]}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, scoreRange: value as [number, number] }))}
                    min={0}
                    max={5}
                    step={0.1}
                    className="w-full"
                  />
                </div>
              </PopoverContent>
            </Popover>

            {/* Date Range Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex h-10 w-28 sm:w-32 md:w-36 shrink-0 items-center justify-between rounded-md border border-input bg-background px-2 sm:px-3 py-2 text-[var(--font-size-sm)] ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {filters.dateRange?.from ? (
                    filters.dateRange.to ? (
                      <span className="line-clamp-1 text-xs sm:text-sm">
                        {format(filters.dateRange.from, "MMM dd")} - {format(filters.dateRange.to, "MMM dd")}
                      </span>
                    ) : (
                      <span className="line-clamp-1 text-xs sm:text-sm">
                        {format(filters.dateRange.from, "MMM dd, y")}
                      </span>
                    )
                  ) : (
                    <>
                      <span className="line-clamp-1 text-xs sm:text-sm hidden sm:inline">Upload Date</span>
                      <span className="line-clamp-1 text-xs sm:text-sm sm:hidden">Date</span>
                    </>
                  )}
                  <CalendarIcon className="h-4 w-4 opacity-50 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={filters.dateRange}
                  onSelect={(range) => setFilters(prev => ({ ...prev, dateRange: range || { from: undefined, to: undefined } }))}
                  numberOfMonths={2}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors shrink-0"
                title="Clear all filters"
              >
                <span>{activeFiltersCount}</span>
                <X className="h-3 w-3" />
              </button>
            )}
            </div>

            {/* Talent Count and View Toggle */}
            <div className="flex items-center gap-2 text-[var(--font-size-sm)] text-gray-600 shrink-0">
              <span className="flex items-center whitespace-nowrap">
                <Users className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">{filteredTalents.length} talents</span>
                <span className="sm:hidden">{filteredTalents.length}</span>
              </span>
              {/* View Toggle */}
              <div className="flex items-center space-x-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="p-2"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="p-2"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-6 overflow-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 className="text-[var(--font-size-lg)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-gray-900 mb-2">Loading talents...</h3>
              <p className="text-gray-600">Fetching talent data from the backend</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="h-12 w-12 text-[var(--error-color)] mx-auto mb-4">
                <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-[var(--font-size-lg)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-gray-900 mb-2">Error loading talents</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : filteredTalents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-[var(--font-size-lg)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] text-gray-900 mb-2">
                {talentCards.length === 0 ? 'No talent cards available' : 'No talents found'}
              </h3>
              <p className="text-gray-600 mb-4">
                {talentCards.length === 0 
                  ? 'Upload talent files to see them processed and displayed here'
                  : 'Try adjusting your search or filters'
                }
              </p>
              {talentCards.length > 0 && (
                <div className="flex justify-center">
                  <SecondaryButton onClick={clearFilters}>Clear all filters</SecondaryButton>
                </div>
              )}
            </div>
          ) : (
            viewMode === 'grid' ? (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                {filteredTalents.map(talent => {
                  const card = talentCards.find(c => c.id === talent.id);
                  if (!card) return null;
                  return (
                    <div
                      key={card.id}
                      className="cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleTalentClick(talent)}
                    >
                      <ApiBasedProfileCard
                        talentData={transformTalentCardToApiFormat(card)}
                        showRadarChart={false}
                        onDelete={handleDeleteTalent}
                        isAdmin={isAdmin}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTalents.map(talent => {
                  const card = talentCards.find(c => c.id === talent.id);
                  if (!card) return null;
                  return (
                    <TalentListItem
                      key={card.id}
                      talentData={transformTalentCardToApiFormat(card)}
                      onClick={(talentId) => navigate(`/talents/${talentId}`)}
                      onDelete={handleDeleteTalent}
                      isAdmin={isAdmin}
                    />
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          refresh(); // Refresh the talent cards list after upload
        }}
      />
    </AppLayout>
  );
};

export default TalentShowcase;